import { OptionType } from '@evinced-private/ui-common';

import { IWebSdkTest } from 'src/apps/web-sdks/types/WebSdkDashboardServiceTypes';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

/**
 * Build a map of display names to original label keys
 * Used to normalize labels that use display names back to their original keys
 */
export const buildDisplayNameToKeyMap = (allLabels: ConfigurationLabel[]): Map<string, string> => {
	const displayNameMap = new Map<string, string>();
	allLabels.forEach((label) => {
		const originalKey = label.label.toLowerCase();
		displayNameMap.set(originalKey, originalKey);
		if (label.displayName) {
			const displayNameLower = label.displayName.toLowerCase();
			displayNameMap.set(displayNameLower, originalKey);
		}
	});
	return displayNameMap;
};

/**
 * Normalize a label key by mapping display names back to original keys
 * @example "Unit ID" → "unitid" (if Unit ID is the display name for unitId)
 */
const normalizeLabelKey = (key: string, displayNameMap?: Map<string, string>): string => {
	const keyLower = key.toLowerCase();
	if (displayNameMap) {
		return displayNameMap.get(keyLower) || keyLower;
	}
	return keyLower;
};

/**
 * Extract the key prefix from a label string (part before ':')
 * Normalizes display names back to original keys if displayNameMap is provided
 * @example "accessibility:pass" → "accessibility"
 * @example "Unit ID:value" → "unitid" (if Unit ID is display name for unitId)
 */
export const extractLabelKey = (label: string, displayNameMap?: Map<string, string>): string => {
	const colonIndex = label.indexOf(':');
	if (colonIndex <= 0) {
		return '';
	}
	const key = label.substring(0, colonIndex).trim();
	return normalizeLabelKey(key, displayNameMap);
};

/**
 * Check if label matches any label key (case-insensitive prefix match)
 * Internal helper for filtering labels
 */
const matchesLabelKey = (
	label: string,
	labelKeys: string[],
	displayNameMap?: Map<string, string>
): boolean => {
	const key = extractLabelKey(label, displayNameMap);
	return key.length > 0 && labelKeys.includes(key);
};

/**
 * Extract unique label keys from test rows
 * Filters by provided label keys (for validation)
 */
export const getExistingLabelKeysFromRows = (
	testRows: IWebSdkTest[],
	filterByKeys?: string[]
): string[] => {
	if (testRows?.length === 0) {
		return [];
	}

	const existingKeys = new Set<string>();
	testRows.forEach((row) => {
		const labels = row?.labels ?? [];
		labels.forEach((label: string) => {
			if (!filterByKeys || matchesLabelKey(label, filterByKeys)) {
				const key = extractLabelKey(label);
				if (key) {
					existingKeys.add(key);
				}
			}
		});
	});

	return Array.from(existingKeys);
};

/**
 * Extract unique labels from test rows
 * Filters by configuration labels
 * Handles both original keys and display names
 */
export const getExistingLabelsFromRows = (
	testRows: IWebSdkTest[],
	allLabels: ConfigurationLabel[]
): string[] => {
	if (!testRows?.length || !allLabels?.length) {
		return [];
	}

	const labelKeys = allLabels.map(({ label }) => label.toLowerCase());
	const displayNameMap = buildDisplayNameToKeyMap(allLabels);

	const uniqueLabels = new Set(
		testRows
			.flatMap((row) => row?.labels ?? [])
			.filter((label) => matchesLabelKey(label, labelKeys, displayNameMap))
	);

	return Array.from(uniqueLabels);
};

/**
 * Parse a label string into key and value parts
 * Normalizes display names back to original keys if displayNameMap is provided
 * @example "environment:production" → { key: "environment", value: "production" }
 * @example "Unit ID:value" → { key: "unitid", value: "value" } (if Unit ID is display name for unitId)
 * @returns null if label format is invalid
 */
export const parseLabel = (
	label: string,
	displayNameMap?: Map<string, string>
): { key: string; value: string } | null => {
	const colonIndex = label.indexOf(':');
	if (colonIndex <= 0) {
		return null;
	}

	const key = label.substring(0, colonIndex).trim();
	const value = label.substring(colonIndex + 1).trim();

	if (!key || !value) {
		return null;
	}

	// Normalize the key to original format
	const normalizedKey = normalizeLabelKey(key, displayNameMap);

	return { key: normalizedKey, value };
};

/**
 * Extract the value from a config value (handles both string and object formats)
 * Internal helper used by transformLabelValuesToOptions
 */
const extractConfigLabelValue = (value: unknown): string =>
	typeof value === 'string' ? value : (value as { value: string }).value;

/**
 * Transform label values to OptionType format
 * If selectedLabelKey is provided, only shows values from the matching label
 */
export const transformLabelValuesToOptions = (
	allLabels: ConfigurationLabel[],
	selectedLabelKey?: string
): OptionType[] => {
	const key = selectedLabelKey ? selectedLabelKey.toLowerCase() : '';

	return allLabels
		.filter((label) => (!key || label.label.toLowerCase() === key) && label.values?.length)
		.flatMap(({ values }) => {
			if (!values) {
				return [];
			}
			return values
				.map((value) => {
					const labelValue = extractConfigLabelValue(value);
					return { value: labelValue, label: labelValue };
				})
				.filter((option) => option.value && option.value.trim().length > 0);
		});
};
